/**
 * NX-4971 — advisory payslip-verifier verdict badge.
 *
 * The verifier is ADVISORY ONLY — it never blocks confirming a draft payroll.
 * The badge degrades gracefully: if a row has no `payslipVerificationRef`
 * (backend job not run yet) it renders a neutral placeholder.
 *
 * The column helper lives in `./verifierColumn` (kept separate so this file only
 * exports components — react-refresh requirement).
 */
import { PayslipVerdict, PayslipVerificationRef } from '@nxsys/types/models/payroll';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  QuestionCircleFilled,
  WarningFilled,
} from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';

type VerdictMeta = {
  /** antd Tag preset colour */
  color: string;
  label: string;
  icon: React.ReactNode;
};

const getVerdictMeta = (verdict?: PayslipVerdict | null): VerdictMeta => {
  switch (verdict) {
    case 'PASS':
      return { color: 'success', label: 'Pass', icon: <CheckCircleFilled /> };
    case 'PASS_WITH_WARNINGS':
      return { color: 'warning', label: 'Warnings', icon: <WarningFilled /> };
    case 'NEEDS_REVIEW':
      return {
        color: 'processing',
        label: 'Review',
        icon: <QuestionCircleFilled />,
      };
    case 'ERRORS_FOUND':
      return { color: 'error', label: 'Errors', icon: <CloseCircleFilled /> };
    default:
      return {
        color: 'default',
        label: 'Unverified',
        icon: <QuestionCircleFilled />,
      };
  }
};

type BadgeProps = {
  verification?: PayslipVerificationRef | null;
  onClick?: () => void;
  /** Larger tag (e.g. the verdict modal header) vs the compact list cell. */
  large?: boolean;
};

export const PayslipVerdictBadge: React.FC<BadgeProps> = ({
  verification,
  onClick,
  large,
}) => {
  const tagStyle: React.CSSProperties | undefined = large
    ? { fontSize: 15, lineHeight: '24px', padding: '2px 12px' }
    : undefined;
  // Advisory: not verified yet. Still clickable (when a handler is given) so the
  // modal can be opened to run verification for the first time.
  if (!verification) {
    return onClick ? (
      <Tooltip title="Not verified — click to run">
        <span className="cursor-pointer" onClick={onClick}>
          _
        </span>
      </Tooltip>
    ) : (
      <span>_</span>
    );
  }

  const { status, verdict, message, groundTruthVerdict, groundTruthMessage } =
    verification;

  // A reviewer's verdict (ground truth) wins over everything — including the AI's verdict
  // and even a failed/queued AI run. It's the authoritative answer once recorded.
  if (groundTruthVerdict) {
    const gtMeta = getVerdictMeta(groundTruthVerdict);
    return (
      <Tooltip title={groundTruthMessage || `Reviewed: ${gtMeta.label}`}>
        <Tag
          icon={gtMeta.icon}
          color={gtMeta.color}
          className={onClick ? 'cursor-pointer' : undefined}
          onClick={onClick}
          style={tagStyle}
        >
          {gtMeta.label} (reviewed)
        </Tag>
      </Tooltip>
    );
  }

  // Queued / running both just show a spinner — no separate status wording.
  if (status === 'QUEUED' || status === 'RUNNING') {
    return <LoadingOutlined spin style={{ fontSize: large ? 20 : 16 }} />;
  }
  if (status === 'FAILED') {
    return (
      <Tooltip title={message || 'Verification failed'}>
        <Tag
          color="default"
          className={onClick ? 'cursor-pointer' : undefined}
          onClick={onClick}
          style={tagStyle}
        >
          Verify failed
        </Tag>
      </Tooltip>
    );
  }

  const meta = getVerdictMeta(verdict);
  return (
    <Tooltip title={message || meta.label}>
      <Tag
        icon={meta.icon}
        color={meta.color}
        className={onClick ? 'cursor-pointer' : undefined}
        onClick={onClick}
        style={tagStyle}
      >
        {meta.label}
      </Tag>
    </Tooltip>
  );
};

export default PayslipVerdictBadge;
